// Lib
import React, { Suspense, lazy, memo, useEffect, useMemo } from 'react';
import { onboardingEntry } from '@modules/Onboarding/entry';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useSelector } from 'react-redux';

import { cn } from '@components/lib/utils';

// Types
import type { RootState } from '@reducers/index';

// Components (eagerly loaded - always visible)
import Sidebar from './Sidebar';
import ManagedPrivateRoute from '@components/ManagedPrivateRoute';
import { LazyAIChat } from '@components/AIDrawer/LazyAIChat';
import AIButton from './AIButton';
import BackgroundTasksTray from './BackgroundTasksTray';
import PageNotFound from './PageNotFound';
// This route shell is tiny; its pages load individually. Keeping the shell
// here avoids a second sequential import before the selected page can load.
import Workspace from '@modules/Workspace';

// Lazy loaded modules (code splitting)
const Auth = lazy(() => import('@modules/Auth'));
const Settings = lazy(() => import('@modules/Settings'));
const Onboarding = lazy(() => import('@modules/Onboarding'));
const ProfileMain = lazy(() =>
  import('@modules/Profile').then((module) => ({
    default: module.ProfileMain,
  })),
);
const Checkout = lazy(() => import('@modules/Checkout'));
const Connect = lazy(() => import('@modules/Auth/components/Connect'));
const Campaigns = lazy(() => import('@modules/Campaigns'));
const Properties = lazy(() => import('@modules/Properties'));
const Dashboard = lazy(() => import('@modules/Dashboard'));
const Offers = lazy(() => import('@modules/Offers'));
const SearchPage = lazy(() => import('@modules/SearchPage'));
const PartnersList = lazy(() => import('@modules/PartnersList'));
const AuthTask = lazy(() => import('@modules/AuthTask'));
const RefetchAuthPosts = lazy(() => import('@modules/RefetchAuthPosts'));
const LinkInBio = lazy(() => import('@modules/LinkInBio'));
const AIChatPage = lazy(() => import('@modules/AIChatPage'));
const Notifications = lazy(() => import('@modules/Notifications'));
const OrganizationInvitations = lazy(
  () => import('@modules/OrganizationInvitations'),
);
const WorkspaceLanding = lazy(() => import('@modules/WorkspaceLanding'));
const CreatorsPage = lazy(
  () => import('@modules/Workspace/pages/CreatorsPage'),
);
const TokenLogin = lazy(() => import('@modules/Auth/components/TokenLogin'));
const Opportunities = lazy(() =>
  import('@modules/PublicCampaigns').then((m) => ({
    default: m.Opportunities,
  })),
);
const PublicBriefPage = lazy(() =>
  import('@modules/PublicCampaigns').then((m) => ({
    default: m.PublicBriefPage,
  })),
);

// Hooks
import useDevice from '@hooks/useDevice';

// Loading fallback for lazy components
const LoadingFallback: React.FC = () => (
  <div className="flex size-full min-h-[200px] items-center justify-center">
    <div className="size-8 animate-spin rounded-full border-[3px] border-marble-bg-100 border-t-marble-blue-500" />
  </div>
);

/** Preserve still-valid emails sent before existing-user invites stopped using
 * registration secrets. All new authenticated auth-route traffic goes home. */
const AuthenticatedAuthRedirect: React.FC = () => {
  const location = useLocation();
  const secret = new URLSearchParams(location.search).get('organizationSecret');
  const isLegacyInvite =
    location.pathname.startsWith('/auth/register') && !!secret;

  return (
    <Navigate
      replace
      to={
        isLegacyInvite
          ? `/organization-invitations?token=${encodeURIComponent(secret)}`
          : '/home'
      }
    />
  );
};

const WRAPPER_CLASS =
  'flex h-screen w-full overflow-hidden max-[1279px]:[&:not(.tablet-fullscreen)]:h-auto max-[1279px]:[&:not(.tablet-fullscreen)]:w-auto max-[1279px]:[&:not(.tablet-fullscreen)]:overflow-auto max-[743px]:[&>.sidebar]:hidden';

/* `isolate` makes this a stacking context so page content stacks against
 * itself and not against the app chrome beside it. Without it the wrapper is
 * `position: relative; z-index: auto`, and any in-page `z-30`/`z-[100]` (the
 * settings save bar, the search history popover) paints over the floating AI
 * panel, which is a sibling at `z-20`. Everything that legitimately covers the
 * chrome — Radix dialogs/sheets/menus, `PostViewModal`, toasts — portals to
 * `document.body` and is unaffected. */
const BODY_WRAPPER_CLASS =
  'relative isolate h-full w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-[1279px]:min-h-screen max-[1279px]:flex-col max-[1279px]:[&>.login-wrapper-two-col]:h-[calc(100vh-80px)] max-[1279px]:[&>.login-wrapper-two-col]:pt-20 max-[743px]:min-h-screen max-[743px]:p-0';

// Tablet body padding — skipped on full-bleed routes and auth.
const NO_PADDING_PATHS = [
  '/settings/advanced',
  '/onboarding/advanced',
  '/onboarding/custom-theme',
  '/task',
];

const ReactUISetup = memo(function Setup(): null {
  const { isLoggedIn, isOnboardingPassed, isOrganizations } = useSelector(
    (state: RootState) => ({
      isLoggedIn: !!state?.Account?._id,
      isOnboardingPassed: state?.Account?.profile?.onboardingPassed,
      isOrganizations:
        state?.Account?.organizations && state?.Account?.organizations?.length,
    }),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const isOrganizationInvitePath =
    pathname.startsWith('/organization-invitations') ||
    (pathname.startsWith('/auth/register') &&
      new URLSearchParams(location.search).has('organizationSecret'));

  const role =
    typeof localStorage === 'object' ? localStorage.getItem('role') : null;

  const isOnlyCreator = useMemo(() => {
    return typeof localStorage === 'object'
      ? !!localStorage.getItem('setupAuthTaskSecret')
      : false;
  }, []);

  useEffect(() => {
    if (isLoggedIn && !isOrganizationInvitePath) {
      if (!isOnboardingPassed) navigate(onboardingEntry());
      if (isOnboardingPassed && !isOrganizations) {
        if (role === 'creator' || isOnlyCreator) {
          navigate('/onboarding/influencer-info');
        } else {
          navigate('/onboarding/start');
        }
      }
    }
  }, [isOnboardingPassed, isLoggedIn, pathname, navigate, isOrganizations, role, isOnlyCreator, isOrganizationInvitePath]);

  return null;
});

/* eslint-disable max-lines-per-function -- Main layout component */
const ReactUI: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const { device } = useDevice();

  const isDesktop = useMemo(
    () =>
      device?.mobile &&
      !device?.mobile() &&
      device?.tablet &&
      !device?.tablet(),
    [device],
  );

  const { isLoggedIn, isOnboardingPassed } = useSelector(
    (state: RootState) => ({
      isLoggedIn: !!state?.Account?._id,
      isOnboardingPassed: !!state?.Account?.profile?.onboardingPassed,
    }),
  );

  const loginRedirect = `/auth/login?returnTo=${encodeURIComponent(
    `${pathname}${location.search}${location.hash}`,
  )}`;

  const isOnboardingPath = pathname?.includes('onboarding');
  const isAIChatPage = pathname?.includes('/ai-chat');

  const isPadding =
    !NO_PADDING_PATHS.includes(pathname) && !pathname?.includes('auth');

  return (
    <>
      <div id="react-ui-wrapper" className={WRAPPER_CLASS}>
        <Sidebar />
        <ReactUISetup />
        <div
          id="body-wrapper"
          className={cn(
            BODY_WRAPPER_CLASS,
            isPadding
              ? 'max-[1279px]:p-[2.5rem_1.875rem_calc(5rem+env(safe-area-inset-bottom))]'
              : 'max-[1279px]:p-0',
          )}
        >
          <Suspense fallback={<LoadingFallback />}>
            {isLoggedIn ? (
              <Routes>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<WorkspaceLanding />} />
                {/*
                  Token sign-in must exist in BOTH route trees. It runs while
                  `isLoggedIn` is still true for the previous occupant of this
                  browser profile, and flips to false mid-flight when the store
                  is reset — the component has to survive that swap. It also
                  has to out-rank `/:id` below, which would otherwise render a
                  public profile for the username "login" (static segments beat
                  dynamic ones in the v6 ranker, so this is already safe).
                */}
                <Route path="/login" element={<TokenLogin />} />
                <Route path="/auth/*" element={<AuthenticatedAuthRedirect />} />
                <Route
                  path="/organization-invitations/:invitationId?"
                  element={<OrganizationInvitations />}
                />
                <Route
                  path="/dashboard/*"
                  element={
                    <ManagedPrivateRoute>
                      <Dashboard />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/*"
                  element={
                    <ManagedPrivateRoute all>
                      <Onboarding />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/settings/*"
                  element={
                    <ManagedPrivateRoute>
                      <Settings />
                    </ManagedPrivateRoute>
                  }
                />
                <Route path="/campaigns/*" element={<Campaigns />} />
                <Route
                  path="/property/*"
                  element={
                    <ManagedPrivateRoute>
                      <Workspace />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/creators/*"
                  element={
                    <ManagedPrivateRoute>
                      <CreatorsPage />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/properties/*"
                  element={
                    <ManagedPrivateRoute>
                      <Properties />
                    </ManagedPrivateRoute>
                  }
                />
                <Route path="/offers/*" element={<Offers />} />
                <Route path="/opportunities" element={<Opportunities />} />
                {/* The shared brief. Registered in both trees (like `/task/*`)
                    because the link is meant to be opened by people with no
                    session; signed in, it additionally shows the eligibility
                    verdict. */}
                <Route path="/c/:slug" element={<PublicBriefPage signedIn />} />
                <Route path="/refetch/:authId" element={<RefetchAuthPosts />} />
                <Route
                  path="/search/*"
                  element={
                    <ManagedPrivateRoute>
                      <SearchPage />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/ai-chat/:sessionId"
                  element={
                    <ManagedPrivateRoute>
                      <AIChatPage />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/ai-chat"
                  element={
                    <ManagedPrivateRoute>
                      <AIChatPage />
                    </ManagedPrivateRoute>
                  }
                />
                <Route path="/saved-creators/*" element={<PartnersList />} />
                <Route
                  path="/checkout/:productId"
                  element={
                    <ManagedPrivateRoute all>
                      <Checkout />
                    </ManagedPrivateRoute>
                  }
                />
                <Route
                  path="/connect"
                  element={
                    <ManagedPrivateRoute all>
                      <Connect />
                    </ManagedPrivateRoute>
                  }
                />
                <Route path="/notifications/*" element={<Notifications />} />
                <Route path="/link-in-bio/*" element={<LinkInBio />} />
                <Route path="/task/*" element={<AuthTask />} />
                <Route path="/404" element={<PageNotFound />} />
                <Route path="/:id/post/:postId" element={<ProfileMain />} />
                <Route path="/:id" element={<ProfileMain />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            ) : (
              <Routes>
                <Route
                  path="/"
                  element={<Navigate to="/auth/login" replace />}
                />
                {/* See the note on the logged-in tree's `/login` route. */}
                <Route path="/login" element={<TokenLogin />} />
                <Route path="/auth/*" element={<Auth />} />
                <Route
                  path="/organization-invitations/:invitationId?"
                  element={<Navigate to={loginRedirect} replace />}
                />
                <Route
                  path="/properties/*"
                  element={<Navigate to={loginRedirect} replace />}
                />
                <Route path="/connect" element={<Connect />} />
                <Route path="/404" element={<PageNotFound />} />
                <Route path="/task/*" element={<AuthTask />} />
                <Route path="/c/:slug" element={<PublicBriefPage />} />
                <Route path="/:id/post/:postId" element={<ProfileMain />} />
                <Route path="/:id" element={<ProfileMain />} />
              </Routes>
            )}
          </Suspense>
        </div>
        {isLoggedIn && isDesktop && isOnboardingPassed && !isOnboardingPath ? (
          <LazyAIChat forceCollapse={isAIChatPage} />
        ) : null}
      </div>
      {isLoggedIn && isOnboardingPassed && !isOnboardingPath ? (
        <>
          <AIButton />
          <BackgroundTasksTray />
        </>
      ) : null}
    </>
  );
};

export default ReactUI;
